import {ApolloClient, ApolloQueryResult, gql} from '@apollo/client';
import {errorResponse, NetworkResult, successResponse} from './NetworkResult';
import {PartialWorkout} from '../Models/PartialWorkout';
import {Duration} from '../Domain/Duration';
import {Distance} from '../Domain/Distance';
import {FullWorkout} from '../Models/FullWorkout';
import {Power} from '../Domain/Power';
import {Fraction} from '../Domain/Fraction';
import {Force} from '../Domain/Force';
import {Energy} from '../Domain/Energy';
import {StrokeRate} from '../Domain/StrokeRate';
import {FULL_WORKOUT_FRAGMENT, PARTIAL_WORKOUT_FRAGMENT, PUBLIC_WORKOUT_FRAGMENT} from './Fragments/Workout';
import {Weight} from '../Domain/Weight';
import {formatISO} from 'date-fns';
import {CrewWeekReport} from '../Models/CrewWeekReport';

export class WorkoutsService {
    constructor(private client: ApolloClient<any>, private publicClient: ApolloClient<any>) {}

    public async update(id:number, title: string): Promise<NetworkResult<PartialWorkout>> {
        try {
            const apolloQueryResult = await this.client.mutate({
                mutation: gql`
                    ${PARTIAL_WORKOUT_FRAGMENT}
                    mutation ($id: Int!, $title: String!) {
                        updateWorkout(id: $id,title: $title) {
                            ...PartialWorkout
                        }
                    }
                `,
                variables: {
                    id,
                    title,
                },
            });
            if (apolloQueryResult.errors) {
                return errorResponse();
            }

            return successResponse(this.getPartialWorkout(apolloQueryResult.data.updateWorkout));
        } catch (e) {
            console.error(e);
            return errorResponse();
        }
    }

    public async updateUser(id: number, newUserId: number): Promise<NetworkResult<PartialWorkout>> {
        try {
            const apolloQueryResult = await this.client.mutate({
                mutation: gql`
                    ${PARTIAL_WORKOUT_FRAGMENT}
                    mutation ($id: Int!, $new_user_id: Int!) {
                        transferWorkout(id: $id, new_user_id: $new_user_id) {
                            ...PartialWorkout
                        }
                    }
                `,
                variables: {
                    id,
                    new_user_id: newUserId,
                },
            });
            if (apolloQueryResult.errors) {
                return errorResponse();
            }

            return successResponse(this.getPartialWorkout(apolloQueryResult.data.transferWorkout));
        } catch (e) {
            return errorResponse();
        }
    }

    public async recent(): Promise<NetworkResult<PartialWorkout[]>> {
        try {
            const apolloQueryResult = await this.client.query({
                query: gql`
                    ${PARTIAL_WORKOUT_FRAGMENT}
                    query {
                        workouts(pageSize: 2) {
                            ...PartialWorkout
                        }
                    }
                `,
            });
            return this.toResponse(apolloQueryResult);
        } catch (e) {
            return errorResponse();
        }
    }

    public async all(): Promise<NetworkResult<PartialWorkout[]>> {
        try {
            const apolloQueryResult = await this.client.query({
                query: gql`
                    ${PARTIAL_WORKOUT_FRAGMENT}
                    query {
                        workouts(all: true) {
                            ...PartialWorkout
                        }
                    }
                `,
            });
            return this.toResponse(apolloQueryResult);
        } catch (e) {
            return errorResponse();
        }
    }

    public async between(from: Date, till: Date): Promise<NetworkResult<PartialWorkout[]>> {
        try {
            const apolloQueryResult = await this.client.query({
                query: gql`
                    ${PARTIAL_WORKOUT_FRAGMENT}
                    query {
                        workouts(from_iso8601: "${formatISO(from)}", till_iso8601: "${formatISO(till)}") {
                            ...PartialWorkout
                        }
                    }
                `,
            });
            return this.toResponse(apolloQueryResult);
        } catch (e) {
            return errorResponse();
        }
    }

    public async thisWeekOfCrew(crewId: number, from: Date, till: Date): Promise<NetworkResult<CrewWeekReport>> {
        try {
            const apolloQueryResult = await this.client.query({
                query: gql`
                    query ($crewId: Int!, $from: String!, $till: String!) {
                        crewWeekReport(crew_id: $crewId, from_iso8601: $from, till_iso8601: $till) {
                            crew_id
                            from_iso8601
                            till_iso8601
                            elapsed_time_millis
                            traveled_distance_millimeters
                            active_members {
                                id
                                display_name
                            }
                            inactive_members {
                                id
                                display_name
                            }
                        }
                    }
                `,
                variables: {
                    crewId,
                    from: formatISO(from),
                    till: formatISO(till),
                },
            });
            return this.toWeekReportResponse(apolloQueryResult);
        } catch (e) {
            return errorResponse();
        }
    }

    public async forCrew(crewId: number, from: Date, till: Date): Promise<NetworkResult<PartialWorkout[]>> {
        try {
            const apolloQueryResult = await this.client.query({
                query: gql`
                    ${PARTIAL_WORKOUT_FRAGMENT}
                    query ($crewId: Int!, $from: String!, $till: String!) {
                        workouts(all: true, crew_id: $crewId, from_iso8601: $from, till_iso8601: $till) {
                            ...PartialWorkout
                        }
                    }
                `,
                variables: {
                    crewId,
                    from: formatISO(from),
                    till: formatISO(till),
                },
            });
            return this.toResponse(apolloQueryResult);
        } catch (e) {
            return errorResponse();
        }
    }

    public async forCrews(
        crewIds: number[],
        from: Date,
        till: Date,
        page: number,
        pageSize: number,
    ): Promise<NetworkResult<PartialWorkout[]>> {
        try {
            const apolloQueryResult = await this.client.query({
                query: gql`
                    ${PARTIAL_WORKOUT_FRAGMENT}
                    query ($crewIds: [Int!], $from: String!, $till: String!, $page: Int!, $pageSize: Int!) {
                        workouts(
                            crew_ids: $crewIds
                            from_iso8601: $from
                            till_iso8601: $till
                            page: $page
                            pageSize: $pageSize
                        ) {
                            ...PartialWorkout
                        }
                    }
                `,
                variables: {
                    crewIds,
                    from: formatISO(from),
                    till: formatISO(till),
                    page: page,
                    pageSize: pageSize,
                },
            });
            return this.toResponse(apolloQueryResult);
        } catch (e) {
            return errorResponse();
        }
    }

    public async partial(id: number): Promise<NetworkResult<PartialWorkout>> {
        try {
            const apolloQueryResult = await this.client.query({
                query: gql`
                    ${PARTIAL_WORKOUT_FRAGMENT}
                    query ($id: Int!) {
                        workout(id: $id) {
                            ...PartialWorkout
                        }
                    }
                `,
                variables: {
                    id,
                },
            });
            return this.toSingleResponse(apolloQueryResult);
        } catch (e) {
            return errorResponse();
        }
    }

    public async full(id: number): Promise<NetworkResult<FullWorkout>> {
        try {
            const apolloQueryResult = await this.client.query({
                query: gql`
                    ${FULL_WORKOUT_FRAGMENT}
                    query ($id: Int!) {
                        workout(id: $id) {
                            ...FullWorkout
                        }
                    }
                `,
                variables: {
                    id,
                },
            });
            if (apolloQueryResult.errors) {
                return errorResponse();
            }

            return successResponse(WorkoutsService.getFullWorkout(apolloQueryResult.data.workout));
        } catch (e) {
            return errorResponse();
        }
    }

    public async fullByToken(id: number, token: string) {
        try {
            const apolloQueryResult = await this.publicClient.query({
                query: gql`
                    ${FULL_WORKOUT_FRAGMENT}
                    query ($id: Int!, $token: String!) {
                        workoutByToken(id: $id, token: $token) {
                            ...FullWorkout
                        }
                    }
                `,
                variables: {
                    id,
                    token,
                },
            });
            if (apolloQueryResult.errors) {
                return errorResponse();
            }

            return successResponse(WorkoutsService.getFullWorkout(apolloQueryResult.data.workoutByToken));
        } catch (e) {
            return errorResponse();
        }
    }

    public async fullByHash(id: number, hash: string): Promise<NetworkResult<FullWorkout>> {
        try {
            const apolloQueryResult = await this.publicClient.query({
                query: gql`
                    ${PUBLIC_WORKOUT_FRAGMENT}
                    query ($id: Int!, $hash: String!) {
                        workoutByHash(id: $id, hash: $hash) {
                            ...FullWorkout
                        }
                    }
                `,
                variables: {
                    id,
                    hash,
                },
            });
            if (apolloQueryResult.errors) {
                return errorResponse();
            }

            return successResponse(WorkoutsService.getFullWorkout(apolloQueryResult.data.workoutByHash));
        } catch (e) {
            return errorResponse();
        }
    }

    public async uploadToStrava(id: number): Promise<NetworkResult<FullWorkout>> {
        try {
            const apolloQueryResult = await this.client.mutate({
                mutation: gql`
                    ${FULL_WORKOUT_FRAGMENT}
                    mutation ($id: Int!) {
                        syncToStrava(id: $id) {
                            ...FullWorkout
                        }
                    }
                `,
                variables: {
                    id,
                },
            });
            if (apolloQueryResult.errors) {
                return errorResponse();
            }

            return successResponse(WorkoutsService.getFullWorkout(apolloQueryResult.data.syncToStrava));
        } catch (e) {
            return errorResponse();
        }
    }

    public async deleteAll(selectedWorkoutIds: number[]) {
        await Promise.all(selectedWorkoutIds.map((id) => this.delete(id)));
    }

    public async delete(id: number) {
        await this.client.mutate({
            mutation: gql`
                mutation ($id: Int!) {
                    deleteWorkoutById(id: $id) {
                        id
                    }
                }
            `,
            variables: {
                id,
            },
        });
    }

    private toResponse(apolloQueryResult: ApolloQueryResult<any>): NetworkResult<PartialWorkout[]> {
        if (apolloQueryResult.errors) {
            return errorResponse();
        }

        return successResponse(
            apolloQueryResult.data.workouts.map((workoutResponse: any) => {
                return this.getPartialWorkout(workoutResponse);
            }),
        );
    }

    private toSingleResponse(apolloQueryResult: ApolloQueryResult<any>): NetworkResult<PartialWorkout> {
        if (apolloQueryResult.errors) {
            return errorResponse();
        }

        return successResponse(this.getPartialWorkout(apolloQueryResult.data.workout));
    }

    private toWeekReportResponse(apolloQueryResult: ApolloQueryResult<any>): NetworkResult<CrewWeekReport> {
        if (apolloQueryResult.errors) {
            return errorResponse();
        }

        const result = {
            crewId: apolloQueryResult.data.crewWeekReport.crew_id,
            elapsedTime: Duration.fromMilliseconds(apolloQueryResult.data.crewWeekReport.elapsed_time_millis),
            traveledDistance: Distance.fromMillimeters(
                apolloQueryResult.data.crewWeekReport.traveled_distance_millimeters,
            ),
            activeMembers: apolloQueryResult.data.crewWeekReport.active_members.map((m: any) => ({
                userId: m.id,
                displayName: m.display_name,
            })),
            inactiveMembers: apolloQueryResult.data.crewWeekReport.inactive_members.map((m: any) => ({
                userId: m.id,
                displayName: m.display_name,
            })),
        };
        return successResponse(result);
    }

    private getPartialWorkout(workoutResponse: any): PartialWorkout {
        return {
            id: workoutResponse.id,
            user_id: workoutResponse.user_id,
            public_user: {
                display_name: workoutResponse.public_user.display_name,
            },
            execution_type: workoutResponse.execution_type,
            executed_at: new Date(workoutResponse.executed_at_iso8601),
            title: workoutResponse.title,
            share_url: workoutResponse.share_url,
            track_geo_url: workoutResponse.track_geo_url,
            track_geo_strokes_url: workoutResponse.track_geo_strokes_url,
            strava_synced: workoutResponse.strava_synced,
            machine_configurations: workoutResponse.machine_configurations.map((machineConfigurationResponse: any) => {
                return {
                    machine_type: machineConfigurationResponse.machine_type,
                    boat_type: machineConfigurationResponse.boat_type,
                    weight: Weight.fromGrams(machineConfigurationResponse.weight_grams),
                };
            }),
            workout_part_results: workoutResponse.workout_part_results.map((workoutPartResultResponse: any) => {
                return {
                    stroke_count: workoutPartResultResponse.stroke_count,
                    elapsed_time: Duration.fromMilliseconds(workoutPartResultResponse.elapsed_time_millis),
                    traveled_distance: Distance.fromMillimeters(
                        workoutPartResultResponse.traveled_distance_millimeters,
                    ),
                    burned_calories:
                        workoutPartResultResponse.burned_calories === null
                            ? null
                            : Energy.fromCalories(workoutPartResultResponse.burned_calories),
                };
            }),
            training_plan: workoutResponse.training_plan,
        };
    }

    public static getFullWorkout(workoutResponse: any): FullWorkout {
        return {
            id: workoutResponse.id,
            original_user_id: workoutResponse.original_user_id,
            user_id: workoutResponse.user_id,
            public_user: {
                display_name: workoutResponse.public_user?.display_name,
            },
            execution_type: workoutResponse.execution_type,
            executed_at: new Date(workoutResponse.executed_at_iso8601),
            title: workoutResponse.title,
            share_url: workoutResponse.share_url,
            track_geo_url: workoutResponse.track_geo_url,
            track_geo_strokes_url: workoutResponse.track_geo_strokes_url,
            track_heart_rate_url: workoutResponse.track_heart_rate_url,
            is_claimed: workoutResponse.is_claimed,
            strava_synced: workoutResponse.strava_synced,
            machine_configurations: workoutResponse.machine_configurations.map((machineConfigurationResponse: any) => {
                return {
                    machine_type: machineConfigurationResponse.machine_type,
                    boat_type: machineConfigurationResponse.boat_type,
                    weight: machineConfigurationResponse.weight_grams
                        ? Weight.fromGrams(machineConfigurationResponse.weight_grams)
                        : null,
                };
            }),
            workout_part_results: workoutResponse.workout_part_results.map((workoutPartResultResponse: any) => {
                return {
                    stroke_count: workoutPartResultResponse.stroke_count,
                    elapsed_time: Duration.fromMilliseconds(workoutPartResultResponse.elapsed_time_millis),
                    traveled_distance: Distance.fromMillimeters(
                        workoutPartResultResponse.traveled_distance_millimeters,
                    ),
                    burned_calories:
                        workoutPartResultResponse.burned_calories === null
                            ? null
                            : Energy.fromCalories(workoutPartResultResponse.burned_calories),
                    average_power:
                        workoutPartResultResponse.average_power_watts === null
                            ? null
                            : Power.fromWatts(workoutPartResultResponse.average_power_watts),
                    average_relative_peak_force_position:
                        workoutPartResultResponse.average_relative_peak_force_position === null
                            ? null
                            : Fraction.fromValue(workoutPartResultResponse.average_relative_peak_force_position),
                    average_split: Duration.fromMilliseconds(workoutPartResultResponse.average_split_millis),
                    average_peak_force:
                        workoutPartResultResponse.average_peak_force === null
                            ? null
                            : Force.fromNewton(workoutPartResultResponse.average_peak_force),
                    average_stroke_length: Distance.fromMillimeters(
                        workoutPartResultResponse.average_stroke_length_millimeters,
                    ),
                    average_energy_per_stroke:
                        workoutPartResultResponse.average_energy_per_stroke === null
                            ? null
                            : Energy.fromJoules(workoutPartResultResponse.average_energy_per_stroke),
                    average_stroke_rate:
                        workoutPartResultResponse.average_stroke_rate === null
                            ? null
                            : StrokeRate.fromStrokesPerMinute(workoutPartResultResponse.average_stroke_rate),
                    average_drive_time:
                        workoutPartResultResponse.average_drive_time_millis === null
                            ? null
                            : Duration.fromMilliseconds(workoutPartResultResponse.average_drive_time_millis),
                    average_recover_time:
                        workoutPartResultResponse.average_recover_time_millis === null
                            ? null
                            : Duration.fromMilliseconds(workoutPartResultResponse.average_recover_time_millis),
                    total_energy:
                        workoutPartResultResponse.total_energy_joules === null
                            ? null
                            : Energy.fromJoules(workoutPartResultResponse.total_energy_joules),
                };
            }),
            training_plan:
                workoutResponse.training_plan === null
                    ? null
                    : {
                          title: workoutResponse.training_plan.title,
                          intervals: workoutResponse.training_plan.intervals.map((intervalResponse: any) => {
                              return {
                                  repetitions: intervalResponse.repetitions,
                                  parts: intervalResponse.parts.map((partResponse: any) => {
                                      return {
                                          type: partResponse.type,
                                      };
                                  }),
                              };
                          }),
                          workouts: workoutResponse.training_plan.workouts.map((otherWorkoutResponse: any) => {
                              return {
                                  id: otherWorkoutResponse.id,
                                  executed_at: new Date(otherWorkoutResponse.executed_at_iso8601),
                              };
                          }),
                      },
        };
    }
}
